import { GraphValue, Graph } from "./graph";
import { SVG } from "./svg";
import { DisplayChart } from "./display_chart";
import { Size } from "./size";

export interface LinegraphOpts {
  render_key: boolean;
  inset: number;
  color: string;
  color_set: string[] | null;
  curve: number;
  style: string;
  render_title: boolean;
}

export interface GraphItem {
  label: SVGElement;
  color_block: SVGElement;
}

export class Linegraph {

  static hover(idx: number, paths: SVGElement[], items: GraphItem[], color: string) {
    const off_color = "#e4e4e4";
    for (let j = 0; j < paths.length; j++) {
      const path = paths[j];
      path.style.fill = j === idx ? color : off_color;
    }
    for (let j = 0; j < items.length; j++) {
      const item = items[j];
      if (j === idx) {
        item.label.style.fill = "#808080";
        item.color_block.style.fill = color;
      } else {
        item.label.style.fill = off_color;
        item.color_block.style.fill = off_color;
      }
    }
  }

  static clear_hover(paths: SVGElement[], items: GraphItem[], color: string) {
    for (let j = 0; j < paths.length; j++) {
      const path = paths[j];
      path.style.fill = color;
    }
    for (let j = 0; j < items.length; j++) {
      const item = items[j];
      item.label.style.fill = "#808080";
      item.color_block.style.fill = color;
    }
  }

  static render(svg: SVGElement, id: string, title: string, data: GraphValue[][], _bucket_labels: string[], opts: LinegraphOpts, ratio: number, container?: HTMLElement): Size {

    const max = Graph.totals(data);
    const width = 400;
    const inset = opts.inset;
    const graph_width = width - (inset * 2);
    const temp_height = 470 / ratio;
    const graph_height = temp_height - 4;
    let title_offset = 0;

    svg.setAttribute("viewBox", "0 0 " + width + " " + temp_height + "");
    svg.classList.add("line-graph");

    if (opts.render_title) {
      Graph.render_title(svg, id, title, width, inset);
      title_offset = 40;
    }

    if (data.length === 0) {
      return {
        width: width,
        height: temp_height,
      };
    }

    const item_count = data[0].length;
    const values: number[][] = [];
    const labels: string[] = [];
    for (let i = 0; i < item_count; i++) {
      const vals = [];
      for (let j = 0; j < data.length; j++) {
        const val = data[j][i];
        if (j === 0) {
          labels.push(val.label);
        }
        vals.push(val.amount);
      }
      values.push(vals);
    }

    const stacked = true;
    if (stacked) {
      for (let i = item_count - 2; i >= 0; i--) {
        const prev = values[i + 1];
        for (let j = 0; j < values[i].length; j++) {
          values[i][j] += prev[j];
        }
      }
    }

    const buckets = data.length;
    const bucket_count = opts.style === "step" ? buckets : buckets - 1;
    const bucket_width = graph_width / bucket_count;

    const graph = document.createElementNS("http://www.w3.org/2000/svg", "g");
    graph.style.transform = "scaleY(-1) translate(" + inset + "px, -" + (graph_height + inset + title_offset) + "px)";
    svg.appendChild(graph);

    const paths: SVGElement[] = [];
    const items: GraphItem[] = [];

    const curve_path = function (vals: number[], item_idx: number, item_count: number): string {
      const c = opts.curve / 100;
      const start_x = 0;
      const start_y = (vals[0] / max.max) * graph_height;
      let px = start_x;
      let py = start_y;
      let d = "M " + start_x + " " + start_y;
      for (let j = 1; j < vals.length; j++) {
        const val = vals[j];
        const x = j * bucket_width;
        const y = (val / max.max) * graph_height;
        const cx1 = px + ((bucket_width / 2) * c);
        const cx2 = x - ((bucket_width / 2) * c);
        const cy1 = py;
        const cy2 = y;
        d += " C " + cx1 + " " + cy1 + " " + cx2 + " " + cy2 + " " + x + " " + y;
        px = x;
        py = y;
      }
      if (item_idx < item_count - 1) {
        const next = values[item_idx + 1];
        const n_start_x = 0;
        const n_start_y = (vals[0] / max.max) * graph_height;
        px = n_start_x;
        py = n_start_y;
        for (let j = next.length - 1; j >= 0; j--) {
          const val = next[j];
          const x = j * bucket_width;
          const y = (val / max.max) * graph_height;
          if (j === next.length - 1) {
            d += " L " + x + " " + y;
          } else {
            const cx1 = px - ((bucket_width / 2) * c);
            const cx2 = x + ((bucket_width / 2) * c);
            const cy1 = py;
            const cy2 = y;
            d += " C " + cx1 + " " + cy1 + " " + cx2 + " " + cy2 + " " + x + " " + y;
          }
          px = x;
          py = y;
        }
      } else { // last item
        d += " L " + graph_width + " " + 0;
        d += " L " + 0 + " " + 0;
      }
      return d;
    }

    const line_path = function (vals: number[], item_idx: number, item_count: number): string {
      const points = [];
      for (let j = 0; j < vals.length; j++) {
        const val = vals[j];
        const x = j * bucket_width;
        const y = (val / max.max) * graph_height;
        points.push(x + " " + y);
      }
      if (item_idx < item_count - 1) {
        const next = values[item_idx + 1];
        for (let j = next.length - 1; j >= 0; j--) {
          const val = next[j];
          const x = j * bucket_width;
          const y = (val / max.max) * graph_height;
          points.push(x + " " + y);
        }
      } else { // last item
        points.push(graph_width + " " + 0);
        points.push(0 + " " + 0);
      }
      const d = "M" + points.join("L");
      return d;
    }

    const add_step_curve = function (idx: number, vals: number[], curve_percent: number, reverse: boolean): string {
      const val = vals[idx];
      let x = idx * bucket_width;
      if (reverse) {
        x = (idx + 1) * bucket_width; // different
      }
      const y = (val / max.max) * graph_height;

      let prev = val;
      if (!reverse && idx > 0) {
        prev = vals[idx - 1];
      }
      if (reverse && idx < vals.length - 1) {
        prev = vals[idx + 1];
      }

      let next = val;
      if (!reverse && idx < vals.length - 1) {
        next = vals[idx + 1];
      }
      if (reverse && idx > 0) {
        next = vals[idx - 1];
      }

      let next_x = (idx + 1) * bucket_width;
      if (reverse) {
        next_x = x - bucket_width; // different
      }
      const prev_y = (prev / max.max) * graph_height;
      const next_y = (next / max.max) * graph_height;
      const dx = Math.abs(x - next_x) / 2;
      const dy1 = Math.abs(y - prev_y) / 2;
      const dy2 = Math.abs(y - next_y) / 2;
      const d1 = (Math.min(dx, dy1) * curve_percent);
      const d2 = (Math.min(dx, dy2) * curve_percent);

      let path = " ";

      const first = (!reverse && idx === 0) || (reverse && idx === vals.length - 1);
      if (first) {
        if (!reverse) {
          path += "M" + x + " " + y;
        }
      } else {
        // draw trailing
        const y_dir1 = prev_y > y ? -1 : 1;
        const cs_x = x;
        const cs_y = y - (d1 * y_dir1);
        let ce_x = x + d1;
        if (reverse) {
          ce_x = x - d1; // different
        }
        const ce_y = y;

        path += "L" + cs_x + " " + cs_y;
        const cx1 = cs_x;
        const cx2 = ce_x;
        const cy1 = cs_y + (d1 * y_dir1);
        const cy2 = ce_y;
        path += " C " + cx1 + " " + cy1 + " " + cx2 + " " + cy2 + " " + ce_x + " " + ce_y;
      }

      const last = (!reverse && idx === vals.length - 1) || (reverse && idx === 0);
      if (!last) {
        const y_dir2 = next_y > y ? -1 : 1;
        let cs_x = x + bucket_width - d2;
        if (reverse) {
          cs_x = x - bucket_width + d2; // different
        }
        const cs_y = y;
        let ce_x = x + bucket_width;
        if (reverse) {
          ce_x = x - bucket_width; // different
        }
        const ce_y = y - (d2 * y_dir2);

        path += "L" + cs_x + " " + cs_y;
        const cx1 = cs_x;
        const cx2 = ce_x;
        const cy1 = cs_y;
        const cy2 = ce_y + (d2 * y_dir2);
        path += " C " + cx1 + " " + cy1 + " " + cx2 + " " + cy2 + " " + ce_x + " " + ce_y;
      } else {
        // add line to end
        const end_x = reverse ? x - bucket_width : x + bucket_width;
        path += "L" + end_x + " " + y;
      }
      return path;
    }

    const step_curve_path = function (vals: number[], item_idx: number, item_count: number, curve_percent: number): string {
      let path = "";
      for (let j = 0; j < vals.length; j++) {
        path += add_step_curve(j, vals, curve_percent, false);
      }

      // bottom edge
      if (item_idx < item_count - 1) {
        const next_item = values[item_idx + 1];
        for (let j = next_item.length - 1; j >= 0; j--) {
          const val = next_item[j];
          const x = (j + 1) * bucket_width;
          const y = (val / max.max) * graph_height;

          // draw right edge
          if (j === next_item.length - 1) {
            path += "L" + x + " " + y;
          }
          path += add_step_curve(j, next_item, curve_percent, true);
        }
      } else { // last item
        path += "L" + graph_width + " " + 0;
        path += "L" + 0 + " " + 0;
      }
      return path;
    }

    const step_path = function (vals: number[], item_idx: number, item_count: number): string {
      const points = [];
      for (let j = 0; j < vals.length; j++) {
        const val = vals[j];
        const x = j * bucket_width;
        const y = (val / max.max) * graph_height;
        points.push(x + " " + y);
        points.push((x + bucket_width) + " " + y);
      }
      if (item_idx < item_count - 1) {
        const next = values[item_idx + 1];
        for (let j = next.length - 1; j >= 0; j--) {
          const val = next[j];
          const x = (j + 1) * bucket_width;
          const y = (val / max.max) * graph_height;
          points.push(x + " " + y);
          points.push((x - bucket_width) + " " + y);
        }
      } else { // last item
        points.push(graph_width + " " + 0);
        points.push(0 + " " + 0);
      }
      const d = "M" + points.join("L");
      return d;
    }

    const c = opts.curve / 100;
    for (let i = 0; i < item_count; i++) {
      let shape_color = opts.color_set ? opts.color_set[i] : opts.color;
      shape_color = "#" + shape_color;
      const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
      p.setAttribute("fill", shape_color);
      paths.push(p);
      graph.appendChild(p);
      const vals = values[i];
      if (opts.style === "step") {
        if (opts.curve > 5) {
          const path = step_curve_path(vals, i, item_count, c);
          p.setAttribute("d", path);
        } else {
          const path = step_path(vals, i, item_count);
          p.setAttribute("d", path);
        }
      } else {
        if (opts.curve > 0) {
          const path = curve_path(vals, i, item_count);
          p.setAttribute("d", path);
        } else {
          const path = line_path(vals, i, item_count);
          p.setAttribute("d", path);
        }
      }
      p.style.fill = shape_color;
      p.style.stroke = "white";
      p.style.strokeWidth = "0.5";
      if (container !== undefined) {
        p.setAttribute("data-color", shape_color);
        p.onmouseover = function () {
          const c = "#" + opts.color;
          DisplayChart.hover(i, container, c);
        }
        p.onmouseout = function () {
          DisplayChart.clearHover(container);
        }
      } else {
        p.onmouseover = function () {
          Linegraph.hover(i, paths, items, shape_color);
        }
        p.onmouseout = function () {
          Linegraph.clear_hover(paths, items, shape_color);
        }
      }
    }

    if (!opts.render_key) {
      return {
        width: width,
        height: temp_height,
      };
    }

    const label_g = document.createElementNS("http://www.w3.org/2000/svg", "g");
    label_g.style.transform = "translate(" + inset + "px, " + (graph_height + title_offset + (inset * 2)) + "px)";
    svg.appendChild(label_g);

    const line_height = 20;
    const switch_point = Math.ceil(labels.length / 2);
    const col_width = (graph_width - inset) / 2;
    const right_col_edge = col_width + inset;
    let left_offset = 0;
    let right_offset = 0;
    const text_width = col_width - 20;

    for (let i = 0; i < labels.length; i++) {
      const x = i < switch_point ? 0 : right_col_edge;
      const y = i < switch_point ? left_offset : right_offset;
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.style.transform = "translate(" + x + "px, " + y + "px)";
      label_g.appendChild(g);
      const name = labels[i];
      const bg = SVG.rect(0, 0, col_width, line_height);
      bg.setAttribute("fill", "white");
      g.appendChild(bg);
      const r = SVG.rect(0, 0, 10, 10);
      let shape_color = opts.color_set ? opts.color_set[i] : opts.color;
      shape_color = "#" + shape_color;
      r.setAttribute("fill", shape_color);
      g.appendChild(r);
      const label = document.createElementNS("http://www.w3.org/2000/svg", "text");
      label.setAttribute("x", (18).toString());
      label.setAttribute("y", (9).toString());
      label.style.fontSize = "11px";
      label.style.fontFamily = SVG.font;
      label.style.textTransform = "uppercase";
      label.setAttribute("fill", "#808080");
      g.appendChild(label);
      if (i < switch_point) {
        left_offset += line_height;
      } else {
        right_offset += line_height;
      }
      const item: GraphItem = {
        label: label,
        color_block: r,
      }
      items.push(item);
      g.onmouseover = function () {
        Linegraph.hover(i, paths, items, shape_color);
      }
      g.onmouseout = function () {
        Linegraph.clear_hover(paths, items, shape_color);
      }

      const words = name.split(/\s+/);
      const line: string[] = [];
      let w: string | undefined = "";
      while (w = words.splice(0, 1)[0]) {
        line.push(w);
        label.innerHTML = line.join(" ");
        if (label.getComputedTextLength() > text_width) {
          line.pop();
          label.innerHTML = line.join(" ") + " ...";
          break;
        }
      }
    }

    const labels_height = Math.max(left_offset, right_offset) - 10;
    const height = inset + title_offset + graph_height + inset + labels_height + inset;
    svg.setAttribute("viewBox", "0 0 " + width + " " + height + "");

    return {
      width: width,
      height: height,
    };
  }

}